type AnalyticsValue = string | number | boolean | null | undefined;
type AnalyticsProperties = Record<string, AnalyticsValue>;
type QueuedEvent = [eventName: string, properties: AnalyticsProperties];
type PostHogClient = typeof import('posthog-js').posthog;

declare global {
  interface Window {
    posthog?: PostHogClient;
  }
}

let initialized = false;
let enabled = false;
let loading = false;
let posthogClient: PostHogClient | null = null;
const queuedEvents: QueuedEvent[] = [];
let browserNameCache: string | null = null;

function getBrowserName() {
  if (browserNameCache) return browserNameCache;
  if (typeof navigator === 'undefined') return 'unknown';
  const agent = navigator.userAgent;
  if (/Edg\//.test(agent)) browserNameCache = 'edge';
  else if (/Chrome\//.test(agent) && !/Chromium\//.test(agent)) browserNameCache = 'chrome';
  else if (/Firefox\//.test(agent)) browserNameCache = 'firefox';
  else if (/Safari\//.test(agent) && !/Chrome\//.test(agent)) browserNameCache = 'safari';
  else browserNameCache = 'unknown';
  return browserNameCache;
}

function containsPrivateIp(value: string) {
  return /\b(10\.\d{1,3}\.\d{1,3}\.\d{1,3}|192\.168\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}|127\.\d{1,3}\.\d{1,3}\.\d{1,3})\b/.test(value);
}

function sanitizeProperties(properties: AnalyticsProperties = {}) {
  const sanitized: AnalyticsProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    const normalizedKey = key.toLowerCase();
    if (/(raw|frame_data|payload|vin|file_path|local_path|device_ip|ip_address)/.test(normalizedKey)) continue;
    if (typeof value === 'string' && containsPrivateIp(value)) continue;
    sanitized[key] = value;
  }
  sanitized.browser_name = getBrowserName();
  return sanitized;
}

export function initAnalytics() {
  if (initialized || loading || typeof window === 'undefined') return initialized;
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) return false;
  enabled = true;
  loading = true;

  void import('posthog-js')
    .then((module) => {
      const posthog = module.posthog;
      posthogClient = posthog;
      window.posthog = posthog;
      posthog.init(key, {
        api_host: (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com',
        capture_pageview: false,
        autocapture: false,
        disable_session_recording: true,
        persistence: 'localStorage+cookie'
      });
      initialized = true;
      loading = false;
      queuedEvents.splice(0).forEach(([eventName, properties]) => track(eventName, properties));
    })
    .catch(() => {
      enabled = false;
      loading = false;
      queuedEvents.length = 0;
    });

  return true;
}

export function track(eventName: string, properties: AnalyticsProperties = {}) {
  if (!enabled) return;
  if (!posthogClient) {
    if (queuedEvents.length < 50) queuedEvents.push([eventName, properties]);
    return;
  }
  posthogClient.capture(eventName, sanitizeProperties(properties));
}

export function trackPageView(path: string) {
  if (!['/analyzer', '/live', '/decode', '/docs'].some((route) => path === route || path.startsWith(`${route}/`))) return;
  track('$pageview', {
    path,
    page_title: typeof document === 'undefined' ? undefined : document.title
  });
}
